import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, Copy, Lock, Plus, RotateCcw, Save, Shield, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hasKey = (perms, key) => perms.includes("*") || perms.includes(key);

const NameDialog = ({ title, initial = "", onClose, onSubmit }) => {
  const [name, setName] = useState(initial);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[380px]" data-testid="role-name-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div><Label htmlFor="role-name">Role name</Label><Input id="role-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="role-name-input" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="role-name-cancel">Cancel</Button>
          <Button onClick={() => name.trim() && onSubmit(name.trim())} disabled={!name.trim()} data-testid="role-name-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmDialog = ({ title, body, confirmLabel = "Confirm", destructive, onClose, onConfirm }) => (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-[420px]" data-testid="roles-confirm-dialog">
      <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{body}</DialogDescription></DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="roles-confirm-cancel">Cancel</Button>
        <Button onClick={onConfirm} className={destructive ? "bg-red-600 hover:bg-red-700" : "bg-blue-700 hover:bg-blue-800"} data-testid="roles-confirm-ok">{confirmLabel}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const AuditLogCard = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const load = (n = limit) => axios.get(`${API}/rbac/audit-log`, { params: { limit: n } }).then((r) => { setRows(r.data.items); setTotal(r.data.total); }).catch(() => toast.error("Failed to load audit log"));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const fmt = (v) => (v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v) : String(v));
  return (
    <Card data-testid="audit-log-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div><CardTitle>Audit log</CardTitle><CardDescription>Who changed roles, permissions, users, or removed records — newest first ({total} entries).</CardDescription></div>
        <Button variant="outline" size="sm" onClick={() => load()} data-testid="audit-log-refresh"><RotateCcw className="w-4 h-4 mr-1" />Refresh</Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {rows.length === 0 ? <p className="text-sm text-slate-500" data-testid="audit-log-empty">No audit entries yet.</p> : (
          <table className="w-full text-xs min-w-[720px]" data-testid="audit-log-table">
            <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-2">When</th><th className="p-2">Who</th><th className="p-2">Action</th><th className="p-2">Target</th><th className="p-2">Before → After</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`audit-row-${r.id}`}>
                  <td className="p-2 whitespace-nowrap text-slate-600">{new Date(r.at).toLocaleString()}</td>
                  <td className="p-2 font-medium text-slate-800">{r.actor}</td>
                  <td className="p-2"><span className="px-1.5 py-0.5 rounded bg-slate-100 font-mono">{r.action}</span></td>
                  <td className="p-2 text-slate-700 break-all">{r.target}</td>
                  <td className="p-2 text-slate-500 break-all max-w-[360px]"><span className="line-through decoration-slate-300">{fmt(r.before)}</span> → {fmt(r.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows.length < total && <Button variant="ghost" size="sm" className="mt-2" onClick={() => { const n = limit + 100; setLimit(n); load(n); }} data-testid="audit-log-more">Show more</Button>}
      </CardContent>
    </Card>
  );
};

export default function RolesPermissions() {
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [draft, setDraft] = useState({}); // roleId -> permissions[]
  const [open, setOpen] = useState({});
  const [dialog, setDialog] = useState(null); // { type: new|clone|rename|delete|reset|save, role }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, r] = await Promise.all([axios.get(`${API}/rbac/permissions`), axios.get(`${API}/rbac/roles`)]);
    setCatalog(c.data.groups);
    setRoles(r.data);
    setDraft(Object.fromEntries(r.data.map((x) => [x.id, x.permissions])));
    setOpen((o) => Object.keys(o).length ? o : Object.fromEntries(c.data.groups.map((g) => [g.group, true])));
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load roles")); }, []);

  const dirty = roles.filter((r) => JSON.stringify([...(draft[r.id] || [])].sort()) !== JSON.stringify([...r.permissions].sort()));
  const toggle = (role, key) => {
    if (role.id === "owner") return;
    setDraft((d) => { const cur = d[role.id] || []; return { ...d, [role.id]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] }; });
  };
  const toggleGroup = (role, group, on) => {
    if (role.id === "owner") return;
    const keys = group.permissions.map((p) => p.key);
    setDraft((d) => { const cur = (d[role.id] || []).filter((k) => !keys.includes(k)); return { ...d, [role.id]: on ? [...cur, ...keys] : cur }; });
  };

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); setDialog(null); await load(); }
    catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : "Request failed"); }
    finally { setBusy(false); }
  };
  const saveAll = () => run(async () => { for (const r of dirty) await axios.put(`${API}/rbac/roles/${r.id}`, { name: r.name, permissions: draft[r.id], description: r.description || "" }); }, `Saved ${dirty.length} role(s)`);

  return (
    <Card data-testid="roles-permissions-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-blue-700" />Roles & Permissions</CardTitle>
          <CardDescription>Tick what each role may do. Owner always has everything. Users inherit their role plus any per-user overrides.</CardDescription>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => setDialog({ type: "new" })} data-testid="role-new-button"><Plus className="w-4 h-4 mr-1" />New role</Button>
          <Button size="sm" disabled={!dirty.length || busy} onClick={() => setDialog({ type: "save" })} className="bg-blue-700 hover:bg-blue-800" data-testid="roles-save-button"><Save className="w-4 h-4 mr-1" />Save{dirty.length ? ` (${dirty.length})` : ""}</Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[900px]" data-testid="roles-matrix">
          <thead>
            <tr>
              <th className="text-left p-2 w-64 align-bottom text-slate-500 font-medium">Permission</th>
              {roles.map((r) => (
                <th key={r.id} className="p-2 align-bottom text-center" data-testid={`role-col-${r.id}`}>
                  <div className="font-semibold text-slate-800 flex items-center justify-center gap-1">{r.id === "owner" && <Lock className="w-3 h-3 text-slate-400" />}{r.name}</div>
                  <div className="text-[11px] text-slate-500" data-testid={`role-users-${r.id}`}>{r.users} user{r.users === 1 ? "" : "s"}</div>
                  <div className="flex justify-center gap-0.5 mt-1">
                    <button type="button" title="Clone" className="p-1 text-slate-500 hover:text-blue-700" onClick={() => setDialog({ type: "clone", role: r })} data-testid={`role-clone-${r.id}`}><Copy className="w-3.5 h-3.5" /></button>
                    {!r.system && <button type="button" title="Rename" className="p-1 text-slate-500 hover:text-blue-700" onClick={() => setDialog({ type: "rename", role: r })} data-testid={`role-rename-${r.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
                    {r.system && r.id !== "owner" && <button type="button" title="Reset to default" className="p-1 text-slate-500 hover:text-amber-600" onClick={() => setDialog({ type: "reset", role: r })} data-testid={`role-reset-${r.id}`}><RotateCcw className="w-3.5 h-3.5" /></button>}
                    {!r.system && <button type="button" title="Delete" className="p-1 text-slate-500 hover:text-red-600" onClick={() => setDialog({ type: "delete", role: r })} data-testid={`role-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.map((g) => {
              const isOpen = open[g.group] !== false;
              return [
                <tr key={g.group} className="bg-slate-50 border-y border-slate-200">
                  <td className="p-2 font-semibold text-slate-700">
                    <button type="button" className="flex items-center gap-1" onClick={() => setOpen({ ...open, [g.group]: !isOpen })} data-testid={`perm-group-toggle-${g.group.replace(/\s+/g, "-").toLowerCase()}`}>
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}{g.group}
                    </button>
                  </td>
                  {roles.map((r) => {
                    const cur = draft[r.id] || [];
                    const n = g.permissions.filter((p) => hasKey(cur, p.key)).length;
                    return (
                      <td key={r.id} className="p-2 text-center">
                        <Checkbox checked={n === g.permissions.length ? true : n > 0 ? "indeterminate" : false} disabled={r.id === "owner"} onCheckedChange={(v) => toggleGroup(r, g, v === true)} data-testid={`perm-group-${r.id}-${g.group.replace(/\s+/g, "-").toLowerCase()}`} />
                      </td>
                    );
                  })}
                </tr>,
                ...(isOpen ? g.permissions.map((p) => (
                  <tr key={p.key} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="p-2 pl-8 text-slate-700">{p.label}<span className="block text-[10px] text-slate-400 font-mono">{p.key}</span></td>
                    {roles.map((r) => (
                      <td key={r.id} className="p-2 text-center">
                        <Checkbox checked={hasKey(draft[r.id] || [], p.key)} disabled={r.id === "owner"} onCheckedChange={() => toggle(r, p.key)} data-testid={`perm-${r.id}-${p.key}`} />
                      </td>
                    ))}
                  </tr>
                )) : []),
              ];
            })}
          </tbody>
        </table>
      </CardContent>

      {dialog?.type === "new" && <NameDialog title="New role" onClose={() => setDialog(null)} onSubmit={(name) => run(() => axios.post(`${API}/rbac/roles`, { name, permissions: [] }), `Role "${name}" created`)} />}
      {dialog?.type === "clone" && <NameDialog title={`Clone "${dialog.role.name}"`} initial={`${dialog.role.name} (copy)`} onClose={() => setDialog(null)} onSubmit={(name) => run(() => axios.post(`${API}/rbac/roles/${dialog.role.id}/clone`, { name }), `Role "${name}" created`)} />}
      {dialog?.type === "rename" && <NameDialog title="Rename role" initial={dialog.role.name} onClose={() => setDialog(null)} onSubmit={(name) => run(() => axios.put(`${API}/rbac/roles/${dialog.role.id}`, { name, permissions: draft[dialog.role.id], description: dialog.role.description || "" }), "Role renamed")} />}
      {dialog?.type === "delete" && <ConfirmDialog title={`Delete role "${dialog.role.name}"?`} body={dialog.role.users ? `This role is assigned to ${dialog.role.users} user(s). Reassign them first.` : "This cannot be undone."} confirmLabel="Delete" destructive onClose={() => setDialog(null)} onConfirm={() => run(() => axios.delete(`${API}/rbac/roles/${dialog.role.id}`), "Role deleted")} />}
      {dialog?.type === "reset" && <ConfirmDialog title={`Reset "${dialog.role.name}" to default?`} body="Permissions return to the built-in defaults for this role. Users keep their personal overrides." confirmLabel="Reset" onClose={() => setDialog(null)} onConfirm={() => run(() => axios.post(`${API}/rbac/roles/${dialog.role.id}/reset`), "Role reset to default")} />}
      {dialog?.type === "save" && <ConfirmDialog title="Save permission changes?" body={`${dirty.map((r) => r.name).join(", ")} will change for every user with ${dirty.length === 1 ? "that role" : "those roles"} immediately.`} confirmLabel="Save changes" onClose={() => setDialog(null)} onConfirm={saveAll} />}
    </Card>
  );
}
